/**
 * 쓸어버리기 팔 비주얼 — 스윕 제스처(RageGestures) 동안 책상 위에 나타나는 저폴리 팔.
 * 포인터를 실시간으로 따라 움직이며(direct manipulation), 이동 속도에 비례해 진행 방향으로
 * 기울어(lean) 역동적으로 휘두르는 느낌을 준다. 손을 떼면 관성 팔로우스루 후 사라진다.
 * dir(+1 = 좌→우 스윕 = 오른팔, -1 = 우→좌 = 왼팔)에 따라 엄지 위치와 실루엣을 미러링한다.
 * 실제 물건 타격은 물리(RageMoves.stepSweep)가 담당 — 이 클래스는 순수 연출.
 */
import * as THREE from 'three';
import { LAYOUT } from '../config/layout';
import type { SweepDir } from '../gameplay/rageLogic';

const SKIN = 0xe8b98a;
const SLEEVE = 0x2f3542;
/** 팔이 떠 있는 높이(책상 윗면 기준) — 물건들을 스치듯 지나가는 높이. */
const ARM_Y = LAYOUT.desk.topY + 0.17;
/** 포인터 추종 스무딩(클수록 즉각적). */
const FOLLOW_LERP = 22;
/** 이동 속도 → 기울기(rad per m/s) + 상한. */
const LEAN_SCALE = 0.06;
const LEAN_MAX = 0.6;
/** 손 뗀 뒤 관성 팔로우스루 지속(초). */
const RELEASE_TIME = 0.18;

export class RageArm {
  private group = new THREE.Group();
  private thumb: THREE.Mesh;
  private state: 'hidden' | 'active' = 'hidden';
  private curX = 0;
  private targetX = 0;
  /** 스무딩된 x 속도(월드 m/s) — 기울기·팔로우스루 방향. */
  private vx = 0;
  private releasing = false;
  private releaseT = 0;

  constructor(scene: THREE.Scene) {
    const part = (
      size: [number, number, number],
      pos: [number, number, number],
      color: number,
    ): THREE.Mesh => {
      const m = new THREE.Mesh(
        new THREE.BoxGeometry(...size),
        new THREE.MeshStandardMaterial({ color, roughness: 0.8 }),
      );
      m.position.set(...pos);
      m.castShadow = true;
      this.group.add(m);
      return m;
    };
    // 손이 화면 안쪽(-z)을 향하는 팔뚝: 소매(플레이어 쪽) → 팔뚝 → 손 + 엄지.
    part([0.17, 0.15, 0.3], [0, 0.02, 0.3], SLEEVE);
    part([0.13, 0.11, 0.38], [0, 0, 0], SKIN);
    part([0.16, 0.08, 0.2], [0, -0.01, -0.27], SKIN);
    this.thumb = part([0.05, 0.06, 0.1], [-0.1, -0.01, -0.22], SKIN);

    // 소품이 실물 대비 1.7~2.4x로 확대된 세계라 팔도 같은 스케일로 키운다(책상 깊이를 덮는 길이).
    this.group.scale.setScalar(1.75);
    this.group.visible = false;
    scene.add(this.group);
  }

  get active(): boolean {
    return this.state !== 'hidden';
  }

  /** 등장 — dir 스윕 방향, 손 시작 X. */
  show(dir: SweepDir, x: number): void {
    this.state = 'active';
    this.curX = x;
    this.targetX = x;
    this.vx = 0;
    this.releasing = false;
    this.releaseT = 0;
    this.thumb.position.x = -0.1 * dir; // 오른팔/왼팔 미러
    this.group.position.set(x, ARM_Y, 0.05);
    // 정면 카메라에서 원근 축소로 상자처럼 보이지 않게 — 실루엣을 대각선으로 비틀어 세운다.
    this.group.rotation.set(0, dir * 0.5, 0);
    this.group.visible = true;
  }

  /** 포인터 X 추종 목표 갱신. */
  follow(x: number): void {
    if (this.state === 'active' && !this.releasing) this.targetX = x;
  }

  /** 손 뗌 — 마지막 속도로 잠깐 미끄러진 뒤(관성) 사라진다. */
  release(): void {
    if (this.state !== 'active') return;
    this.releasing = true;
    this.releaseT = 0;
  }

  hide(): void {
    this.state = 'hidden';
    this.releasing = false;
    this.group.visible = false;
  }

  /** 매 프레임(렌더 dt) 애니메이션. */
  update(dt: number): void {
    if (this.state === 'hidden') return;
    if (this.releasing) {
      this.releaseT += dt;
      this.targetX += this.vx * dt; // 관성으로 계속 미끄러짐
      if (this.releaseT >= RELEASE_TIME) {
        this.hide();
        return;
      }
    }
    const step = Math.min(1, dt * FOLLOW_LERP);
    const nx = this.curX + (this.targetX - this.curX) * step;
    const inst = (nx - this.curX) / Math.max(dt, 1e-3);
    this.vx = this.vx * 0.7 + inst * 0.3; // 속도 스무딩(기울기 떨림 방지)
    this.curX = nx;
    this.group.position.x = this.curX;
    this.group.position.y = ARM_Y;
    // 진행 방향으로 기울기(휘두르는 느낌). y회전 실루엣은 유지.
    this.group.rotation.z = -THREE.MathUtils.clamp(this.vx * LEAN_SCALE, -LEAN_MAX, LEAN_MAX);
  }
}
